import React from 'react';
import { FiEye, FiEdit, FiTrash2, FiKey, FiChevronDown } from 'react-icons/fi';

function getRoleBadgeClass(role) {
  switch (role) {
    case 'ceo':
      return 'role-badge ceo';
    case 'co_ceo':
      return 'role-badge co-ceo';
    case 'company_manager':
      return 'role-badge company-manager';
    case 'sales_head':
      return 'role-badge sales-head';
    case 'sales_employee':
      return 'role-badge sales-employee';
    case 'production_head':
      return 'role-badge production-head';
    case 'production_employee':
      return 'role-badge production-employee';
    case 'manager':
      return 'role-badge manager';
    default:
      return 'role-badge employee';
  }
}

const ROLE_OPTIONS = (
  <>
    <option value="employee">Employee</option>
    <option value="manager">Manager</option>
    <option value="sales_employee">Sales Employee</option>
    <option value="sales_head">Sales Head</option>
    <option value="production_employee">Production Employee</option>
    <option value="production_head">Production Head</option>
    <option value="company_manager">Company Manager</option>
    <option value="co_ceo">CO-CEO</option>
    <option value="ceo">CEO</option>
    <option value="client">Client</option>
  </>
);

/**
 * Single row of the Users Management table, extracted so it can be used
 * both as a memoized React component and as the row renderer for
 * VirtualizedList. `style` comes straight from react-window (position,
 * width, height) and must land on the row's own root element since List
 * renders each row as a `position: absolute; width: 100%` block.
 *
 * Renders as a compact grid row on wide screens and as a stacked card on
 * narrow ones (`isMobile`), matching the taller `rowHeight` AdminPanel
 * passes to VirtualizedList in that mode.
 */
function AdminUserRow({ u, style, isCEO, canManageUsers, isMobile, onUpdateRole, navigate, onViewPassword, onDeleteUser }) {
  const roleControl = isCEO ? (
    <div className="role-select-wrapper">
      <select
        value={u.role}
        onChange={(e) => onUpdateRole(u._id, e.target.value)}
        className={getRoleBadgeClass(u.role)}
      >
        {ROLE_OPTIONS}
      </select>
      <FiChevronDown className="role-select-caret" aria-hidden="true" />
    </div>
  ) : (
    <span className={getRoleBadgeClass(u.role)}>
      {u.role.replace('_', ' ').toUpperCase()}
    </span>
  );

  const actions = (
    <div className="admin-user-actions">
      <button
        className="icon-btn icon-btn-view"
        onClick={() => navigate(`/dashboard/user-detail/${u._id}`)}
        title="View Complete Details"
        aria-label="View Complete Details"
      >
        <FiEye />
      </button>
      {canManageUsers && (
        <button
          className="icon-btn icon-btn-edit"
          onClick={() => navigate(`/dashboard/edit-profile/${u._id}`)}
          title="Edit Employee Profile"
          aria-label="Edit Employee Profile"
        >
          <FiEdit />
        </button>
      )}
      {isCEO && (
        <>
          <button
            className="icon-btn icon-btn-password"
            onClick={() => onViewPassword(u)}
            title="View/Change Password"
            aria-label="View/Change Password"
          >
            <FiKey />
          </button>
          <button
            className="icon-btn icon-btn-delete"
            onClick={() => onDeleteUser(u._id, `${u.firstName} ${u.lastName}`)}
            title="Delete user"
            aria-label="Delete user"
          >
            <FiTrash2 />
          </button>
        </>
      )}
    </div>
  );

  if (isMobile) {
    return (
      <div className="admin-user-card-wrapper" style={style}>
        <div className="admin-user-card">
          <div className="admin-user-card-header">
            <div className="admin-user-card-name" title={`${u.firstName} ${u.lastName}`}>
              {u.firstName} {u.lastName}
            </div>
            {roleControl}
          </div>
          <div className="admin-user-card-body">
            <div className="admin-user-card-row">
              <span className="admin-user-card-label">Email</span>
              <span className="admin-user-card-value" title={u.email}>{u.email}</span>
            </div>
            <div className="admin-user-card-row">
              <span className="admin-user-card-label">Username</span>
              <span className="admin-user-card-value">@{u.username}</span>
            </div>
            <div className="admin-user-card-row">
              <span className="admin-user-card-label">Department</span>
              <span className={`department-badge ${u.department}`}>{u.department || 'general'}</span>
            </div>
            <div className="admin-user-card-row">
              <span className="admin-user-card-label">Status</span>
              <span className={`status-indicator ${u.status}`}>{u.status}</span>
            </div>
            <div className="admin-user-card-row">
              <span className="admin-user-card-label">Projects</span>
              <span className="admin-user-card-value">{u.assignedProjects?.length || 0}</span>
            </div>
          </div>
          {actions}
        </div>
      </div>
    );
  }

  return (
    <div className="admin-user-row" style={style}>
      <div className="admin-user-cell" title={`${u.firstName} ${u.lastName}`}>
        {u.firstName} {u.lastName}
      </div>
      <div className="admin-user-cell" title={u.email}>
        {u.email}
      </div>
      <div className="admin-user-cell">@{u.username}</div>
      <div className="admin-user-cell">{roleControl}</div>
      <div className="admin-user-cell">
        <span className={`department-badge ${u.department}`}>
          {u.department || 'general'}
        </span>
      </div>
      <div className="admin-user-cell">
        <span className={`status-indicator ${u.status}`}>{u.status}</span>
      </div>
      <div className="admin-user-cell">{u.assignedProjects?.length || 0} projects</div>
      <div className="admin-user-cell admin-user-actions-cell">
        {actions}
      </div>
    </div>
  );
}

export default React.memo(AdminUserRow);
